import { hasPermission, hasRole, isSuperAdmin } from '@/utils/auth'

/**
 * 菜单/路由权限配置：path 对应路由，permission 为进入该页所需权限码（满足其一）。
 * 与后端 @HasPermission 的 page/list 类权限对齐。
 */
export const MENU_PERMISSION_MAP = {
  '/user': 'user:page',
  '/role': 'role:page',
  '/permission': 'permission:list',
  '/student': 'student:page',
  '/campus': 'campus:page',
  '/course': 'course:page',
  '/class': 'class:page',
  '/schedule': 'schedule:page',
  '/teacher-schedule': 'teacherSchedule:list',
  '/class-duty': 'classDuty:list',
  '/homework-standard': 'homeworkStandard:page',
  '/homework': 'homework:page',
  '/homework-commission': 'homeworkCommission:page',
  '/exam-question': 'examQuestion:page',
  '/exam-paper': 'examPaper:page',
  '/exam-test': 'examTest:page',
  '/exam-review': 'examReview:page',
  '/product': 'marketProduct:page',
  '/prepayment-order': 'marketPrepaymentOrder:page',
  '/order': 'marketOrder:page',
  '/refund-order': 'marketOrder:refundPage',
  '/knowledge-base': 'knowledgeBase:page',
  '/knowledge-chunks': 'knowledgeBase:page',
  '/knowledge-recall': 'knowledgeBase:page',
  '/knowledge-qa': 'knowledgeBase:page',
  '/prompt-templates': 'promptTemplate:page',
  '/prompt-tests': ['promptTest:page', 'promptTest:execute'],
  '/text-to-sql-analysis': 'textToSqlAnalysis:query',
  '/text-to-sql-runs': 'textToSqlRun:page',
  '/text-to-sql-permissions': 'textToSqlPermission:page',
  '/text-to-sql-eval-samples': 'textToSqlEvalQuestion:page',
  '/text-to-sql-eval-tasks': 'textToSqlEvalTask:page',
  '/config': ['configType:list', 'configItem:list']
}

/** 登录后默认首页：按菜单顺序找第一个有权限的页面；都没有则返回 null */
export function getDefaultHomePath() {
  const order = Object.keys(MENU_PERMISSION_MAP)
  for (const path of order) {
    if (hasPermission(MENU_PERMISSION_MAP[path])) {
      return path
    }
  }
  return null
}

/** 当前路由是否可访问 */
export function canAccessPath(path) {
  if (!path) return true
  // 去掉 query/hash，只匹配 path
  const pure = String(path).split('?')[0].split('#')[0]
  const permission = MENU_PERMISSION_MAP[pure]
  if (!permission) return true
  return hasPermission(permission)
}

/**
 * 将扁平权限列表按编码前缀分组，供角色赋权树使用。
 * @param {Array<{id:number,permissionCode:string,permissionName:string,status?:string}>} list
 */
export function buildPermissionTree(list) {
  const groups = {}
  ;(list || []).forEach(item => {
    if (!item || item.id == null) return
    const code = item.permissionCode || ''
    const prefix = code.includes(':') ? code.split(':')[0] : (code || 'other')
    if (!groups[prefix]) {
      groups[prefix] = {
        id: `group:${prefix}`,
        label: prefix,
        children: []
      }
    }
    const statusTag = item.status && item.status !== 'ACTIVE' ? '（禁用）' : ''
    groups[prefix].children.push({
      id: Number(item.id),
      label: `${item.permissionName || code}${statusTag}`,
      code,
      disabled: item.status === 'INACTIVE'
    })
  })
  return Object.keys(groups)
    .sort()
    .map(key => {
      const group = groups[key]
      group.children.sort((a, b) => String(a.code).localeCompare(String(b.code)))
      return group
    })
}

export { hasPermission, hasRole, isSuperAdmin }

